import React, { useState, useEffect } from 'react'
import { Button, Select, Table, Space, Modal, Form, message } from 'antd'
import { PlusOutlined, DeleteOutlined, EyeOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import { useLanguage } from '../../../components/context/LanguageContext'
import { apiRequest } from '../../../helpers'

const { Option } = Select
const { confirm } = Modal

const ChatbotStorageSelector = ({ value = [], onChange, formRef }) => {
  const { t } = useLanguage()
  const [selectedStorages, setSelectedStorages] = useState([])
  const [availableStorages, setAvailableStorages] = useState([])
  const [selectedStorageId, setSelectedStorageId] = useState(null)
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)

  // Fetch all available storage connectors
  useEffect(() => {
    const fetchStorages = async () => {
      setLoading(true)
      try {
        // Try the standard API endpoint first
        let response = await apiRequest(`${process.env.REACT_APP_API}connector/storage`)

        // If that fails, try the chatbot-specific endpoint that might be provided by chatbot agent service
        if (!response.ok || !response.data || !response.data.items) {
          console.log('Trying alternative storage endpoint')
          response = await apiRequest(`${process.env.REACT_APP_API}agents/chatbot/_storages`)
        }

        if (response.ok && response.data) {
          const items = response.data.items || []
          console.log('Loaded storage connectors:', items.length)
          setAvailableStorages(items)
        } else {
          console.error('Failed to load storage connectors from both endpoints')
          message.warning(t('Storage connectors could not be loaded'))
          setAvailableStorages([])
        }
      } catch (error) {
        console.error('Error fetching storages:', error)
        message.warning(t('Storage connectors could not be loaded'))
        setAvailableStorages([])
      } finally {
        setLoading(false)
      }
    }

    fetchStorages()
  }, [t])

  // Load selected storages when value changes or when availableStorages loads
  useEffect(() => {
    const loadSelectedStorages = async () => {
      if (!value || !value.length) {
        setSelectedStorages([])
        return
      }

      setLoading(true)
      try {
        // If value is already an array of objects, use it directly
        if (typeof value[0] === 'object') {
          setSelectedStorages(value)
          return
        }

        // Convert to array if needed
        const storageIds = Array.isArray(value) ? value : [value]

        // First try to match with available storages (to avoid extra API calls)
        if (availableStorages.length > 0) {
          const foundStorages = storageIds.map(id => availableStorages.find(s => s.id === id)).filter(Boolean)

          // If we found all the storages in available list, use them
          if (foundStorages.length === storageIds.length) {
            console.log('Using available storages for selected list:', foundStorages)
            setSelectedStorages(foundStorages)
            setLoading(false)
            return
          }
        }

        // If we couldn't find all storages in the available list, create simplified objects
        // instead of making API calls that might fail
        const simplifiedStorages = storageIds.map(id => {
          // Try to get from available storages first
          const found = availableStorages.find(s => s.id === id)
          if (found) return found

          // If not found, create a simple object with just the ID
          // Extract the type from the ID format (e.g., "url-1" => "url")
          const type = id.split('-')[0] || 'unknown'
          return {
            id: id,
            name: `${type.charAt(0).toUpperCase() + type.slice(1)} Connector (${id})`,
            type: type,
            active: true,
            canUpload: true,
            canDelete: true,
            canProcessMultiple: true,
          }
        })

        console.log('Using simplified storage objects:', simplifiedStorages)
        setSelectedStorages(simplifiedStorages)
      } catch (error) {
        console.error('Error loading selected storages:', error)
        message.warning(t('Some storage connectors could not be loaded correctly'))
      } finally {
        setLoading(false)
      }
    }

    loadSelectedStorages()
  }, [value, availableStorages, t])

  // Add a storage connector
  const addStorage = () => {
    if (!selectedStorageId) {
      message.warning(t('Please select a storage connector'))
      return
    }

    // Check if already added
    if (selectedStorages.some(s => s.id === selectedStorageId)) {
      message.warning(t('This storage connector is already added'))
      setModalVisible(false)
      return
    }

    // Find the selected storage from available storages
    const storageToAdd = availableStorages.find(s => s.id === selectedStorageId)
    if (!storageToAdd) {
      message.error(t('Selected storage not found'))
      return
    }

    // Create a new array with all existing storages plus the new one
    const newSelectedStorages = [...selectedStorages, storageToAdd]
    console.log(
      'Updated storage connectors:',
      newSelectedStorages.map(s => s.id)
    )

    // Update form value (only send the IDs)
    const newStorageIds = newSelectedStorages.map(s => s.id)

    // First update the local state
    setSelectedStorages(newSelectedStorages)

    // Then update the form value after a brief delay to ensure state is updated
    onChange(newStorageIds)

    setModalVisible(false)
    setSelectedStorageId(null)
  }

  // Remove a storage connector
  const removeStorage = storageId => {
    confirm({
      title: t('Remove Storage Connector'),
      icon: <ExclamationCircleOutlined />,
      content: t('Are you sure you want to remove this storage connector?'),
      okText: t('Yes'),
      okType: 'danger',
      cancelText: t('No'),
      onOk: () => {
        // Create a new array without the removed storage
        const newSelectedStorages = selectedStorages.filter(s => s.id !== storageId)
        console.log(
          'Remaining storage connectors after removal:',
          newSelectedStorages.map(s => s.id)
        )

        // Update form value (only send the IDs)
        const newStorageIds = newSelectedStorages.map(s => s.id)

        // First update the local state
        setSelectedStorages(newSelectedStorages)

        // Then update the form value
        onChange(newStorageIds)
      },
    })
  }

  // View storage connector details
  const viewStorage = storage => {
    const storageType = storage.type || (storage.baseUrl ? 'url' : 'server')
    Modal.info({
      title: t('Storage Connector Details'),
      width: 600,
      content: (
        <div>
          <p>
            <strong>{t('Name')}:</strong> {storage.name}
          </p>
          <p>
            <strong>{t('Type')}:</strong> {storageType}
          </p>
          {storage.baseUrl && (
            <p>
              <strong>{t('Base URL')}:</strong> {storage.baseUrl}
            </p>
          )}
          <p>
            <strong>{t('Can Upload')}:</strong> {storage.canUpload ? t('Yes') : t('No')}
          </p>
          <p>
            <strong>{t('Can Delete')}:</strong> {storage.canDelete ? t('Yes') : t('No')}
          </p>
          <p>
            <strong>{t('Can Process Multiple')}:</strong> {storage.canProcessMultiple ? t('Yes') : t('No')}
          </p>
          <p>
            <strong>{t('Active')}:</strong> {storage.active ? t('Yes') : t('No')}
          </p>
        </div>
      ),
      onOk() {},
    })
  }

  const columns = [
    {
      title: t('Name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('Type'),
      key: 'type',
      render: (text, record) => record.type || (record.baseUrl ? 'url' : 'server'),
    },
    {
      title: t('Active'),
      dataIndex: 'active',
      key: 'active',
      render: active => (active ? t('Yes') : t('No')),
    },
    {
      title: t('Actions'),
      key: 'actions',
      render: (text, record) => (
        <Space size="middle">
          <Button type="text" icon={<EyeOutlined />} onClick={() => viewStorage(record)} />
          <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeStorage(record.id)} />
        </Space>
      ),
    },
  ]

  return (
    <div className="chatbot-storage-selector">
      <Table
        dataSource={selectedStorages}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={false}
        locale={{ emptyText: t('No storage connectors selected') }}
        size="small"
      />

      <Button type="dashed" icon={<PlusOutlined />} onClick={() => setModalVisible(true)} style={{ marginTop: 16 }}>
        {t('Add Storage Connector')}
      </Button>

      <Modal
        title={t('Add Storage Connector')}
        open={modalVisible}
        onOk={addStorage}
        onCancel={() => {
          setModalVisible(false)
          setSelectedStorageId(null)
        }}
      >
        <Select style={{ width: '100%' }} placeholder={t('Select a storage connector')} value={selectedStorageId} onChange={setSelectedStorageId}>
          {availableStorages
            .filter(s => !selectedStorages.some(added => added.id === s.id))
            .map(storage => (
              <Option key={storage.id} value={storage.id}>
                {storage.name} ({storage.type || (storage.baseUrl ? 'url' : 'server')})
              </Option>
            ))}
        </Select>
      </Modal>
    </div>
  )
}

export default ChatbotStorageSelector
